/**
 * SubscriptionContext — Backend-Driven Subscription State
 * 
 * SINGLE SOURCE OF TRUTH: Backend API -> MongoDB subscription_transactions
 * 
 * All reads come from GET /api/subscription/clinic/{clinicId}
 * All writes go to backend first, then refresh from API.
 * localStorage is NOT used as source of truth.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import api from '../utils/api';

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children, clinicId }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);
  const prevClinicIdRef = useRef(null);

  // ── READ: Fetch current subscription from backend ──
  const fetchSubscription = useCallback(async (cId) => {
    const id = cId || clinicId;
    if (!id) {
      setLoading(false);
      return null;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/subscription/clinic/${id}`);
      const data = res.data;
      if (data.has_subscription) {
        setSubscription(data);
      } else {
        setSubscription(null);
      }
      return data;
    } catch (err) {
      setError(err.message);
      setSubscription(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  // Fetch on mount when clinicId is available
  useEffect(() => {
    if (clinicId && clinicId !== prevClinicIdRef.current) {
      prevClinicIdRef.current = clinicId;
      fetchedRef.current = true;
      fetchSubscription(clinicId);
    }
    if (!clinicId) {
      setSubscription(null);
      setLoading(false);
      fetchedRef.current = false;
      prevClinicIdRef.current = null;
    }
  }, [clinicId, fetchSubscription]);

  // ── WRITE: Start trial / Extend trial ──
  // REMOVED (MTMR-P0 B1.1). The platform trial-administration endpoints
  // (subscription trial start + trial extend) are lifecycle-administration
  // surfaces and are refused for Provider callers. The launch trial is now
  // provisioned server-side at the canonical Provider activation transition
  // (`ProviderLifecycleAuthority.provision_launch_subscription`), so the
  // Provider app must never create or extend subscription state.
  // Consumer grep at removal time: the only reference was the auto-start
  // effect in ClinicAdminDashboard.jsx (removed in the same change);
  // the extend helper had zero call sites.

  // ── WRITE: Activate/upgrade paid subscription ──
  // TREATX PROTOCOL: Paid activation happens ONLY through webhook/reconciliation.
  // This function triggers reconciliation and then refreshes state.
  const activateSubscription = useCallback(async (packageKey) => {
    if (!clinicId) throw new Error('No clinic ID');
    // Refresh subscription state from backend (webhook may have already activated)
    await fetchSubscription(clinicId);
    return subscription;
  }, [clinicId, fetchSubscription, subscription]);

  // Backward-compatible alias
  const upgradeSubscription = useCallback(async (packageId) => {
    return activateSubscription(packageId);
  }, [activateSubscription]);

  // ── Derived state (uses NORMALIZED uppercase statuses from backend) ──
  const status = subscription?.status || 'NONE';
  const hasSubscription = !!subscription?.has_subscription;
  const isTrial = status === 'TRIALING';
  const isActive = status === 'ACTIVE' || status === 'TRIALING';
  const isPastDue = status === 'PAST_DUE';
  const packageKey = subscription?.package_key || null;

  // Trial status (derived, not a function — backward compatible)
  const trialStatus = useMemo(() => {
    if (!isTrial || !subscription?.trial) return null;
    const trial = subscription.trial;
    const now = new Date();
    const endDate = new Date(trial.end_date);
    const startDate = new Date(trial.start_date);
    const totalDays = trial.days || 14;
    const daysRemaining = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
    const daysUsed = totalDays - daysRemaining;
    return {
      isActive: daysRemaining > 0,
      isLastDay: daysRemaining === 1,
      daysRemaining,
      daysUsed,
      totalDays,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      extended: trial.extended || false,
      percentUsed: Math.min(100, Math.round((daysUsed / totalDays) * 100)),
    };
  }, [isTrial, subscription]);

  const trialExpired = isTrial && trialStatus && !trialStatus.isActive;
  const needsTrialStart = !hasSubscription && !loading;

  // Package snapshot (limits + features frozen at subscription time)
  // FIX P0-A: Normalize snake_case limit keys from backend to camelCase for frontend components
  const packageSnapshot = useMemo(() => {
    if (!subscription) return null;
    const rawLimits = subscription.limits || {};
    const normalizedLimits = {
      leads: rawLimits.leads_per_month ?? rawLimits.leads ?? rawLimits.leadsPerMonth ?? 0,
      salesReps: rawLimits.sales_reps ?? rawLimits.salesReps ?? 0,
      offersPerMonth: rawLimits.offers_per_month ?? rawLimits.offersPerMonth ?? 0,
      campaignsActiveMax: rawLimits.campaigns_active_max ?? rawLimits.campaignsActiveMax ?? 0,
      campaignsCreateMonthlyMax: rawLimits.campaigns_create_monthly_max ?? rawLimits.campaignsCreateMonthlyMax ?? 0,
      leadsMonthlyMax: rawLimits.leads_monthly_max ?? rawLimits.leadsMonthlyMax ?? 0,
      activeThreadsMax: rawLimits.active_threads_max ?? rawLimits.activeThreadsMax ?? 0,
      quotesMonthlyMax: rawLimits.quotes_monthly_max ?? rawLimits.quotesMonthlyMax ?? 0,
      // Preserve raw keys for any component that reads snake_case directly
      ...rawLimits,
    };
    return {
      id: subscription.package_key,
      packageKey: subscription.package_key,
      name: subscription.package_name || subscription.package_key,
      limits: normalizedLimits,
      features: subscription.features || {},
      amount: subscription.amount,
      currency: subscription.currency,
    };
  }, [subscription]);

  // currentPackage — backward compatible with FeatureGatedWrapper
  const currentPackage = packageSnapshot;

  // Feature gate
  const canUseFeature = useCallback((featureKey) => {
    if (!hasSubscription) return false;
    const features = subscription?.features || {};
    return !!features[featureKey];
  }, [hasSubscription, subscription]);

  const hasFeature = canUseFeature;

  // Limit check
  const getLimit = useCallback((limitKey) => {
    if (!hasSubscription) return 0;
    return subscription?.limits?.[limitKey] ?? 0;
  }, [hasSubscription, subscription]);

  // Usage tracking (backend-driven)
  const [usage, setUsage] = useState({});
  const updateUsage = useCallback(async () => {
    if (!clinicId) return;
    try {
      const res = await api.get(`/clinics/${clinicId}/usage`);
      if (res.data) setUsage(res.data);
    } catch {
      // Usage endpoint may not exist yet — that's OK
    }
  }, [clinicId]);

  const checkUsageLimit = useCallback((limitKey) => {
    const limit = getLimit(limitKey);
    const used = usage[limitKey] || 0;
    if (limit <= 0) return { allowed: true, current: used, max: limit };
    return { allowed: used < limit, current: used, max: limit };
  }, [getLimit, usage]);

  const canPerformAction = useCallback((actionType) => {
    if (!hasSubscription) return false;
    return true;
  }, [hasSubscription]);

  const value = {
    // State
    subscription,
    loading,
    error,
    hasSubscription,
    status,
    isTrial,
    isActive,
    isPastDue,
    packageKey,
    trialStatus,
    trialExpired,
    needsTrialStart,
    packageSnapshot,
    currentPackage,
    usage,
    // Actions
    activateSubscription,
    upgradeSubscription,
    refreshSubscription: fetchSubscription,
    updateUsage,
    // Helpers
    canUseFeature,
    hasFeature,
    getLimit,
    checkUsageLimit,
    canPerformAction,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
